import React, { useEffect, useState } from 'react';
import { MultiSelect } from '../ui/MultiSelect';
import {
  listPushResortOptions,
  type PushAudienceFilters,
  type PushAudienceType,
  type PushCampaignEstimate,
  type PushOrderCount,
  type PushOrderState,
  type PushResortOption,
} from '../../services/resortAdminApi';
import { PUSH_LANGUAGE_OPTIONS, PUSH_PLATFORM_OPTIONS } from '../../utils/pushCampaignValidation';
import { EstimateCard } from './EstimateCard';

const AUDIENCE_OPTIONS: { value: PushAudienceType; label: string; description: string; }[] = [
  { value: 'test_list', label: 'Test list', description: 'Existing app users found by the addresses listed below.' },
  { value: 'app_users', label: 'App users', description: 'Tourists using the client application.' },
  { value: 'partners', label: 'Partners', description: 'Restaurant and grocery staff using the kitchen application.' },
  { value: 'couriers', label: 'Couriers', description: 'Couriers using the delivery application.' },
];

const ORDER_STATE_OPTIONS: { value: PushOrderState; label: string; }[] = [
  { value: 'any', label: 'Any order history' },
  { value: 'never_ordered', label: 'Never placed a paid order' },
  { value: 'ordered_paid', label: 'Placed at least one paid order' },
  { value: 'unpaid_only', label: 'Only unpaid orders' },
  { value: 'dormant', label: 'Ordered before, nothing recently' },
];

const ORDER_COUNT_OPTIONS: { value: PushOrderCount; label: string; }[] = [
  { value: 'any', label: 'Any number of orders' },
  { value: '0', label: 'No orders' },
  { value: '1', label: 'Exactly one order' },
  { value: '2+', label: 'Two orders or more' },
];

const SELECT_CLASS_NAME =
  'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-alps-primary focus:outline-none';

interface AudienceStepProps {
  campaignName: string;
  audienceType: PushAudienceType;
  filters: PushAudienceFilters;
  testEmails: string;
  savedCampaignName: string | null;
  errors: string[];
  onCampaignNameChange: (value: string) => void;
  onAudienceTypeChange: (value: PushAudienceType) => void;
  onFiltersChange: (value: PushAudienceFilters) => void;
  onTestEmailsChange: (value: string) => void;
  onEstimateChange: (estimate: PushCampaignEstimate | null) => void;
}

export const AudienceStep: React.FC<AudienceStepProps> = props => {
  const [resortOptions, setResortOptions] = useState<PushResortOption[]>([]);
  const [resortError, setResortError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listPushResortOptions()
      .then(x => {
        if (cancelled) {
          return;
        }
        setResortOptions(x);
        setResortError(null);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setResortOptions([]);
        setResortError('The resort list could not be loaded, the resort filter is unavailable.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filters = props.filters;
  const patchFilters = (patch: Partial<PushAudienceFilters>) => {
    props.onFiltersChange({ ...filters, ...patch });
  };
  const hasResortFilter = props.audienceType !== 'test_list';

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700">Campaign name</label>
        <input
          type="text"
          value={props.campaignName}
          onChange={x => props.onCampaignNameChange(x.target.value)}
          placeholder="Internal name, not shown to users"
          className="mt-1 block w-full max-w-lg rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-alps-primary focus:outline-none"
        />
      </div>

      <div>
        <span className="block text-sm font-medium text-gray-700">Audience</span>
        <div className="mt-2 space-y-2">
          {AUDIENCE_OPTIONS.map(x => (
            <label
              key={x.value}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${
                props.audienceType === x.value ? 'border-alps-primary bg-alps-primary/5' : 'border-gray-200'
              }`}
            >
              <input
                type="radio"
                name="push-audience-type"
                value={x.value}
                checked={props.audienceType === x.value}
                onChange={() => props.onAudienceTypeChange(x.value)}
                className="mt-1"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">{x.label}</span>
                <span className="block text-xs text-gray-500">{x.description}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      {props.audienceType === 'test_list' && (
        <div>
          <label className="block text-sm font-medium text-gray-700">Test addresses</label>
          <textarea
            value={props.testEmails}
            onChange={x => props.onTestEmailsChange(x.target.value)}
            rows={3}
            placeholder="address@example.com, another@example.com"
            className="mt-1 block w-full max-w-lg rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-alps-primary focus:outline-none"
          />
          <p className="mt-1 text-xs text-gray-500">
            Comma separated. Each address is matched against existing app users; addresses that
            match nobody are reported as skipped.
          </p>
        </div>
      )}

      {hasResortFilter && (
        <div className="space-y-4 rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-900">Filters</h3>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Resorts</label>
              <MultiSelect
                options={resortOptions.map(x => ({ value: x.value, label: x.label }))}
                selected={filters.resorts}
                onChange={x => patchFilters({ resorts: x })}
                placeholder="All resorts"
                disabled={resortOptions.length === 0}
              />
              <p className="mt-1 text-xs text-gray-500">
                {resortError ?? 'Empty means every resort.'}
              </p>
            </div>

            {props.audienceType === 'app_users' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Order history</label>
                <select
                  value={filters.order_state}
                  onChange={x => patchFilters({ order_state: x.target.value as PushOrderState })}
                  className={SELECT_CLASS_NAME}
                >
                  {ORDER_STATE_OPTIONS.map(x => (
                    <option key={x.value} value={x.value}>{x.label}</option>
                  ))}
                </select>
              </div>
            )}

            {props.audienceType === 'app_users' && filters.order_state === 'dormant' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Nothing ordered for, days</label>
                <input
                  type="number"
                  min={1}
                  value={filters.order_state_days ?? ''}
                  onChange={x => patchFilters({ order_state_days: readDays(x.target.value) })}
                  placeholder="30"
                  className={SELECT_CLASS_NAME}
                />
              </div>
            )}

            {props.audienceType === 'app_users' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Number of orders</label>
                  <select
                    value={filters.order_count}
                    onChange={x => patchFilters({ order_count: x.target.value as PushOrderCount })}
                    className={SELECT_CLASS_NAME}
                  >
                    {ORDER_COUNT_OPTIONS.map(x => (
                      <option key={x.value} value={x.value}>{x.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Languages</label>
                  <MultiSelect
                    options={PUSH_LANGUAGE_OPTIONS}
                    selected={filters.languages}
                    onChange={x => patchFilters({ languages: x })}
                    placeholder="All languages"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Platforms</label>
                  <MultiSelect
                    options={PUSH_PLATFORM_OPTIONS}
                    selected={filters.platforms}
                    onChange={x => patchFilters({ platforms: x })}
                    placeholder="All platforms"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Not seen for, days</label>
                  <input
                    type="number"
                    min={1}
                    value={filters.not_seen_for_days ?? ''}
                    onChange={x => patchFilters({ not_seen_for_days: readDays(x.target.value) })}
                    placeholder="Any"
                    className={SELECT_CLASS_NAME}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Registered from</label>
                  <input
                    type="date"
                    value={filters.registered_from ?? ''}
                    onChange={x => patchFilters({ registered_from: readDate(x.target.value) })}
                    className={SELECT_CLASS_NAME}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Registered to</label>
                  <input
                    type="date"
                    value={filters.registered_to ?? ''}
                    onChange={x => patchFilters({ registered_to: readDate(x.target.value) })}
                    className={SELECT_CLASS_NAME}
                  />
                </div>
              </>
            )}
          </div>

          {props.audienceType !== 'app_users' && (
            <p className="text-xs text-gray-500">
              Only the resort filter applies to this audience.
            </p>
          )}
        </div>
      )}

      <EstimateCard
        audienceType={props.audienceType}
        filters={filters}
        campaignName={props.savedCampaignName}
        onEstimateChange={props.onEstimateChange}
      />

      {props.errors.length > 0 && (
        <ul className="space-y-1 border-l-4 border-red-500 bg-red-50 p-4 text-sm text-red-700">
          {props.errors.map(x => <li key={x}>{x}</li>)}
        </ul>
      )}
    </div>
  );
};

function readDays(value: string): number | null {
  if (value.trim() === '') {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return null;
  }
  return Math.trunc(parsed);
}

function readDate(value: string): string | null {
  if (value === '') {
    return null;
  }
  return value;
}
